import { useCallback, useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { invalidateAgentTaskQueries } from './agent-task-invalidation';

const PREPARE_NOW_WINDOW_MS = 15 * 60_000;

export type GenerateDraftOutcome = 'ok' | 'refused' | 'failed';

export function generateDraftOutcomeForStatus(status: number): GenerateDraftOutcome {
  if (status >= 200 && status < 300) return 'ok';
  return status === 409 ? 'refused' : 'failed';
}

export interface GenerateDraftFlow {
  generate: () => Promise<GenerateDraftOutcome>;
  pending: boolean;
  started: boolean;
  failed: boolean;
}

export function useGenerateDraft(taskId: string): GenerateDraftFlow {
  const [pending, setPending] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  const generate = useCallback(async (): Promise<GenerateDraftOutcome> => {
    setPending(true);
    setFailed(false);
    try {
      const res = await apiFetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/generate-draft`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const outcome = generateDraftOutcomeForStatus(res.status);
      if (outcome === 'ok') setStartedAt(Date.now());
      if (outcome === 'failed') setFailed(true);
      else invalidateAgentTaskQueries(taskId);
      return outcome;
    } catch {
      setFailed(true);
      return 'failed';
    } finally {
      setPending(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (startedAt === null) return undefined;
    const expiry = setTimeout(() => setStartedAt(null), Math.max(0, startedAt + PREPARE_NOW_WINDOW_MS - Date.now()));
    return () => clearTimeout(expiry);
  }, [startedAt]);

  return { generate, pending, started: startedAt !== null, failed };
}
